import { useEffect, useRef, useState } from 'react';
import { SearchCandidateProfileMessageSender } from 'generated-graphql/types';
import { SearchCandidateVectorQueryMessage } from 'modules/candidate-profile/types/SearchCandidateVectorQueryMessage';
import {
  SourcingReplyRequest,
  SourcingSearchResponse
} from 'modules/candidate-profile/types/CandidateSearchFilter';
import { generateSourcingReply } from 'services/sourcingSearch/api';
import { logger } from 'core/utils/logger';

// The endpoint's cap: the reply describes the top of the list, not all of it.
const REPLY_CANDIDATE_IDS = 25;

export const buildReplyRequest = (
  response: SourcingSearchResponse,
  searchId: string,
  userMessageId: string,
  messageBody: string
): SourcingReplyRequest => {
  const candidates = response.candidates ?? [];

  return {
    id: searchId,
    userMessageId,
    candidateProfileIds: candidates
      .slice(0, REPLY_CANDIDATE_IDS)
      .map((candidate) => candidate.candidate_profile_id as string),
    totalCandidatesCount: response.totalCandidatesCount ?? 0,
    returnedCandidatesCount: candidates.length,
    semanticQuery: response.generatedSemanticQuery ?? null,
    likeCandidateNames: response.likeCandidateNames ?? [],
    // Not echoed by the endpoint; it is what the recruiter typed.
    booleanQuery: response.searchMode === 'boolean' ? messageBody : null,
    generatedFilter: response.generatedFilter ?? null,
    appliedFilter: response.appliedFilter ?? null
  };
};

type UseSourcingReplyProps = {
  addMessages: (messages: SearchCandidateVectorQueryMessage[]) => void;
};

/** Asks for the agent's chat reply once a turn's results are on screen. */
export const useSourcingReply = ({ addMessages }: UseSourcingReplyProps) => {
  const [isReplying, setIsReplying] = useState(false);
  // A newer turn can start mid-reply; only the latest one's reply is kept.
  const latestTurnIdRef = useRef<string | null>(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const requestReply = async (request: SourcingReplyRequest) => {
    const turnId = request.userMessageId ?? null;
    const isLatestTurn = () =>
      isMountedRef.current && latestTurnIdRef.current === turnId;

    latestTurnIdRef.current = turnId;
    setIsReplying(true);

    try {
      const { newAgentMessage } = await generateSourcingReply(request);
      if (!newAgentMessage || !isLatestTurn()) return;

      addMessages([
        {
          id: newAgentMessage.id,
          body: newAgentMessage.body,
          sender: newAgentMessage.sender as SearchCandidateProfileMessageSender,
          date: newAgentMessage.createdAt ?? new Date().toISOString()
        }
      ]);
    } catch (error) {
      // No toast: the results are already on screen.
      logger.error('Sourcing reply failed', { error });
    } finally {
      if (isLatestTurn()) setIsReplying(false);
    }
  };

  return { isReplying, requestReply };
};
